/**
 * PLATINUM — Cart drawer upsell (v1)
 *
 * Replaces the Wisepops cart drawer cross-sell embed with a deterministic,
 * master-file-driven recommendation block.
 *
 * DATA
 *   assets/platinum-upsell-map.json — generated from platinum-products-master.md
 *   by scripts/build_upsell_map.js. Format: { sourceHandle: [{handle, reason, pitch?}] }
 *   Regenerate + re-upload when cross-sells in the master change.
 *
 * BEHAVIOR
 *   - Renders up to MAX_CARDS recommendation cards at the end of the cart
 *     drawer items list (same slot the Wisepops embed used).
 *   - Candidates: for each cart line (in order), its mapped targets (in order),
 *     deduped, excluding products already in cart and unavailable products.
 *   - "Dodaj" replicates the theme's native add flow (bundled sections +
 *     cart:change dispatch) so drawer, free shipping bar and header count
 *     all update natively without reload.
 *   - Re-renders on cart:change; re-injects after the drawer replaces its
 *     content (cart-drawer:refreshed) since that wipes our container.
 *
 * CONFIG injected by snippets/platinum-cart-upsell.liquid via
 * window.PlatinumUpsellConfig = { mapUrl, heading, maxCards }
 */

(function () {
  'use strict';

  const CFG = window.PlatinumUpsellConfig || {};
  const MAP_URL = CFG.mapUrl;
  const HEADING = CFG.heading || 'Dopolni svojo izbiro';
  const MAX_CARDS = CFG.maxCards || 3;
  const CONTAINER_CLASS = 'platinum-upsell';

  if (!MAP_URL) return;

  let mapPromise = null;
  const productCache = {};
  let rendering = false;
  let renderQueued = false;
  let giftRemoving = false;   // guards the auto-removal from re-firing mid-flight
  let giftJustRemoved = false;

  const moneyFmt = new Intl.NumberFormat(
    (window.Shopify && Shopify.locale) || 'sl-SI',
    { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }
  );

  function getMap() {
    if (!mapPromise) {
      mapPromise = fetch(MAP_URL)
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({}));
    }
    return mapPromise;
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return productCache[handle];
  }

  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return await r.json();
    } catch (e) {
      return { items: [] };
    }
  }

  /**
   * Pick candidate handles from the map for the current cart.
   * Map format v2: { src: { pet: 'dog'|'cat'|null, targets: [...] } } — v1 arrays tolerated.
   * Also collects the pet types of matched cart items so the heading can address
   * the right animal ("Tvoj pes …" / "Tvoja muca …").
   */
  function pickCandidates(map, cart) {
    const inCart = new Set((cart.items || []).map((i) => i.handle));
    const seen = new Set();
    const out = [];
    const petTypes = new Set();
    for (const item of cart.items || []) {
      const entry = map[item.handle];
      if (!entry) continue;
      const targets = Array.isArray(entry) ? entry : entry.targets || [];
      if (!Array.isArray(entry) && entry.pet) petTypes.add(entry.pet);
      for (const t of targets) {
        if (inCart.has(t.handle) || seen.has(t.handle)) continue;
        seen.add(t.handle);
        out.push(t);
      }
    }
    return { targets: out, petTypes };
  }

  /** Heading addressing the right animal; generic for mixed/unknown carts. */
  function pickHeading(petTypes) {
    if (petTypes.size === 1) {
      const pet = petTypes.values().next().value;
      if (pet === 'dog' && CFG.headingDog) return CFG.headingDog;
      if (pet === 'cat' && CFG.headingCat) return CFG.headingCat;
    }
    return HEADING;
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
  }

  /** Replicate the theme's native add-to-cart flow (theme.js ProductForm). */
  async function addVariant(variantId, button, source) {
    source = source || 'cart-drawer';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const body = new FormData();
      body.set('id', String(variantId));
      body.set('quantity', '1');
      // hidden attribution property (underscore prefix = not shown to customer,
      // Prestige line-item.liquid skips it) — lands on the order line item, so
      // upsell-attributed orders/revenue are measurable in Shopify/Supabase
      body.set('properties[_platinum_upsell]', source);
      body.set('sections', sectionsToBundle.join(','));
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      const responseJson = await response.json();
      if (!response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
      // GA4: gtag is defined globally at runtime by the Google & YouTube channel
      // pixel (lax mode) — same defensive pattern as the PFF quiz (proven to land
      // in GA4: quiz_* events visible in reports)
      try {
        if (typeof gtag === 'function') {
          gtag('event', source === 'cart-drawer' ? 'upsell_add' : 'gift_add', {
            event_category: 'PLATINUM Upsell',
            source: source,
            variant_id: String(variantId),
          });
        }
      } catch (e) {}
      // custom event for Shopify Custom Pixels (Settings -> Customer events) —
      // a pixel subscribing to 'platinum_upsell_add' can forward it elsewhere
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('platinum_upsell_add', {
            variantId,
            source: source,
          });
        }
      } catch (e) {}
      // cart PAGE has no live re-render in Prestige (theme reloads on change) — mirror that
      if (document.querySelector('.cart-page')) {
        window.location.reload();
        return;
      }
      const cartContent = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      cartContent['sections'] = responseJson['sections'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        })
      );
    } catch (e) {
      console.warn('[Platinum Upsell] add failed:', e);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function buildCard(target, product) {
    const variants = (product.variants || []).filter((v) => v.available);
    if (!variants.length) return null;
    // default to the cheapest available variant (lowest entry barrier in the drawer);
    // a gap card (Stars promo) preselects the size that closes the gap
    let variant = (target.variantId && variants.find((v) => Number(v.id) === Number(target.variantId))) ||
      variants.reduce((min, v) => (v.price < min.price ? v : min), variants[0]);

    // Horizontal card: image | (title / pitch / variant+price) | add button
    const card = document.createElement('div');
    card.className = 'platinum-upsell__card';

    const link = document.createElement('a');
    link.href = product.url || '/products/' + product.handle;
    link.className = 'platinum-upsell__media';
    const img = document.createElement('img');
    img.src = imageUrl(product.featured_image, 240);
    img.alt = product.title;
    img.loading = 'lazy';
    link.appendChild(img);
    card.appendChild(link);

    const title = document.createElement('a');
    title.href = link.href;
    title.className = 'platinum-upsell__title';
    // drop the category suffix ("MeatCrisp Adult Fish, Suha Hrana za Mačke" -> "MeatCrisp Adult Fish")
    title.textContent = product.title.split(',')[0].trim();
    title.title = product.title;
    card.appendChild(title);

    const pitch = document.createElement('p');
    pitch.className = 'platinum-upsell__pitch';
    pitch.textContent = target.pitch || target.reason || '';
    card.appendChild(pitch);

    const options = document.createElement('div');
    options.className = 'platinum-upsell__options';
    const hasRealVariants = variants.length > 1 && variants[0].title !== 'Default Title';
    if (hasRealVariants) {
      const select = document.createElement('select');
      select.className = 'platinum-upsell__select';
      select.setAttribute('aria-label', 'Izberi velikost');
      for (const v of variants) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      }
      select.value = String(variant.id);
      select.addEventListener('change', () => {
        variant = variants.find((v) => String(v.id) === select.value) || variant;
        priceValue.textContent = moneyFmt.format(variant.price / 100);
      });
      options.appendChild(select);
    } else if (variant.title && variant.title !== 'Default Title') {
      const size = document.createElement('span');
      size.className = 'platinum-upsell__variant';
      size.textContent = variant.title;
      options.appendChild(size);
    }
    // variant choice + price share one row (horizontal card layout)
    const row = document.createElement('div');
    row.className = 'platinum-upsell__row';
    if (options.childNodes.length) row.appendChild(options);

    const price = document.createElement('div');
    price.className = 'platinum-upsell__price';
    const priceValue = document.createElement('span');
    priceValue.textContent = moneyFmt.format(variant.price / 100);
    price.appendChild(priceValue);
    row.appendChild(price);
    card.appendChild(row);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'platinum-upsell__add';
    btn.textContent = 'Dodaj';
    btn.addEventListener('click', () => addVariant(variant.id, btn));
    card.appendChild(btn);

    return card;
  }

  /**
   * Gift block ("2× MENU 185 g gratis"). The Shopify automatic discount does the
   * zeroing — this block only lets the customer pick flavours and put them in the
   * cart, because Shopify never adds a free product by itself.
   *
   * The threshold is measured on NON-gift lines at their pre-discount price, so
   * the block does not flip off the moment the 5 € discount lands and lowers the
   * subtotal. That also keeps our condition stricter than Shopify's, never looser
   * — we can't promise a gift the discount would refuse.
   */
  /** MENU is dog food — never dangle it in front of a cat-only cart. */
  function isCatOnly(cart, map) {
    const items = cart.items || [];
    if (!items.length) return false;
    return items.every((it) => {
      const entry = map && map[it.handle];
      const pet = entry && !Array.isArray(entry) ? entry.pet : null;
      if (pet) return pet === 'cat';
      return /ma[čc]k/i.test((it.product_type || '') + ' ' + (it.product_title || it.title || ''));
    });
  }

  /** Dominant species of the cart by PAID value — picks which gift tier applies. */
  function classifySpecies(cart, map) {
    let dog = 0;
    let cat = 0;
    for (const item of cart.items || []) {
      if (isGiftLine(item)) continue;
      const entry = map && map[item.handle];
      let pet = entry && !Array.isArray(entry) ? entry.pet : null;
      if (!pet && /ma[čc]k|meatcrisp/i.test((item.product_type || '') + ' ' + (item.product_title || item.title || ''))) pet = 'cat';
      if (pet === 'cat') cat += item.final_line_price;
      else if (pet === 'dog') dog += item.final_line_price;
    }
    if (!dog && !cat) return null;
    return cat > dog ? 'cat' : 'dog';
  }

  /**
   * v4 GIFT LADDER (2026-08-21) — CUMULATIVE, one gift for everyone.
   * Tiers unlock by threshold and STACK (kapa @49 € stays when majica @119 €
   * is added). Per tier:
   *   - single variant (kapa)  -> auto-added silently the moment the tier is
   *     reached (no click); if the customer deletes it by hand we don't re-add
   *     it this session.
   *   - several variants (majica sizes, MENU flavours) -> picker, customer
   *     chooses; "zamenjaj" to swap.
   * A tier's gift is taken out again when the cart drops under that tier.
   * Stock-checked per variant; an empty tier vanishes. Optional tier.onlyPet.
   * Per-tier discountCode (MENU test) falls back to the ladder code.
   */
  let autoAdding = false;
  const ssKey = (t, what) => 'ptb-' + what + ':' + (t.name || t.threshold);

  async function ladderState(cart, map, L) {
    if (!(cart.items || []).length) return null;
    if (L.endsAt && Date.now() > Date.parse(L.endsAt)) return null;
    const species = classifySpecies(cart, map);
    const tiers = (L.tiers || [])
      .filter((t) => t.enabled !== false && Array.isArray(t.variants) && t.variants.length &&
        (!t.onlyPet || t.onlyPet === species) && !(t.endsAt && Date.now() > Date.parse(t.endsAt)))
      .slice()
      .sort((a, b) => a.threshold - b.threshold);
    if (!tiers.length) return null;

    const tierOf = new Map();
    tiers.forEach((t) => t.variants.forEach((v) => tierOf.set(Number(v.id), t)));
    const ids = new Set(tierOf.keys());
    const allVariants = tiers.flatMap((t) => t.variants);

    let base = 0;
    const linesByTier = new Map();
    for (const item of cart.items || []) {
      const vid = Number(item.variant_id);
      if (ids.has(vid) && isGiftLine(item)) {
        const t = tierOf.get(vid);
        if (!linesByTier.has(t)) linesByTier.set(t, []);
        linesByTier.get(t).push(item);
      } else {
        base += item.final_line_price; // what the customer PAYS (same basis as the theme's bar)
      }
    }
    const baseEur = base / 100;
    const reached = (t) => baseEur + 0.005 >= t.threshold;

    // 1) gifts of tiers that are no longer reached -> out (only lines WE added)
    const stale = new Set();
    tiers.forEach((t) => {
      if (!reached(t) && (linesByTier.get(t) || []).length) {
        t.variants.forEach((v) => stale.add(Number(v.id)));
        try { sessionStorage.removeItem(ssKey(t, 'auto')); } catch (e) {}
      }
    });
    if (stale.size) {
      if (!giftRemoving) {
        giftRemoving = true;
        giftJustRemoved = true;
        removeGiftLines(stale).finally(() => { giftRemoving = false; });
      }
      return { ladder: true, cfg: ladderCfg(L, null, allVariants, null), mode: 'removed' };
    }
    giftJustRemoved = false;

    const unlocked = tiers.filter(reached);
    const next = tiers.find((t) => !reached(t)) || null;

    // 2) per-tier status
    const rows = [];
    for (const t of unlocked) {
      const lines = linesByTier.get(t) || [];
      const slots = Number(t.slots || L.slots || 1);
      const inCart = lines.reduce((a, i) => a + i.quantity, 0);
      const chosen = [];
      lines.forEach((i) => {
        const cfgV = t.variants.find((v) => Number(v.id) === Number(i.variant_id));
        for (let n = 0; n < i.quantity; n++) chosen.push(cfgV ? cfgV.title : i.variant_title);
      });
      const codeApplied = lines.some((i) => (i.line_level_discount_allocations || []).length > 0);
      const available = [];
      for (const v of t.variants) {
        const product = v.handle ? await getProduct(v.handle) : null;
        const match = product && (product.variants || []).find((pv) => Number(pv.id) === Number(v.id));
        if (product && match && !match.available) continue;
        available.push({
          id: v.id, handle: v.handle, title: v.title,
          name: v.name || (product ? product.title.split(',')[0].trim() : v.title),
          desc: v.desc || '',
          image: (match && match.featured_image && match.featured_image.url) || (product && product.featured_image) || '',
          url: product ? product.url || '/products/' + v.handle : '/products/' + v.handle,
          price: v.price != null ? Number(v.price) : (match && match.price != null ? match.price / 100 : null),
          discountCode: t.discountCode || L.discountCode || '',
          tierName: t.name || '',
        });
      }
      const autoAdd = t.autoAdd === true || (t.autoAdd !== false && available.length === 1 && slots === 1);
      let userRemoved = false;
      try {
        if (autoAdd && inCart === 0 && sessionStorage.getItem(ssKey(t, 'auto'))) {
          sessionStorage.setItem(ssKey(t, 'removed'), '1');
        }
        userRemoved = !!sessionStorage.getItem(ssKey(t, 'removed'));
      } catch (e) {}
      let status;
      if (inCart > 0 && !codeApplied) {
        // self-heal once: re-apply the code (stale session / race) before calling it refused
        let retried = false;
        try { retried = !!sessionStorage.getItem(ssKey(t, 'retry')); } catch (e) {}
        if (!retried) {
          try { sessionStorage.setItem(ssKey(t, 'retry'), '1'); } catch (e) {}
          const code = t.discountCode || L.discountCode;
          if (code) applyDiscountCode(code, true).then(() => document.dispatchEvent(new CustomEvent('cart:refresh')));
          status = 'healing';
        } else {
          status = 'refused';
        }
      }
      else if (inCart >= slots) status = 'done';
      else if (!available.length) status = 'empty';
      else if (autoAdd) status = userRemoved ? 'skipped' : 'auto';
      else status = 'pick';
      rows.push({
        tier: t, status, variants: available, slots, inCart, remaining: Math.max(0, slots - inCart),
        chosen, codeApplied, autoAdd, ids: new Set(t.variants.map((v) => Number(v.id))),
      });
    }

    // 3) silent auto-add (one per pass; cart:change re-renders and continues)
    const autoRow = rows.find((r) => r.status === 'auto');
    if (autoRow && !autoAdding) {
      autoAdding = true;
      autoAddGift(autoRow).finally(() => { autoAdding = false; });
      return { cfg: ladderCfg(L, autoRow.tier, allVariants, next), mode: 'adding', ladder: true, rows: [], next };
    }

    const visible = rows.filter((r) => r.status !== 'auto' && r.status !== 'skipped' && r.status !== 'empty' && r.status !== 'healing');
    if (!visible.length && !next) return null;
    const top = unlocked[unlocked.length - 1] || next;
    return {
      ladder: true, cfg: ladderCfg(L, top, allVariants, next), mode: 'ladder', rows: visible, next,
      missing: next ? next.threshold - baseEur : 0, baseEur,
      hasPick: visible.some((r) => r.status === 'pick'),
    };
  }

  async function autoAddGift(row) {
    const v = row.variants[0];
    try { sessionStorage.setItem(ssKey(row.tier, 'auto'), String(v.id)); } catch (e) {}
    const ghost = document.createElement('button');
    const onCartPage = !!document.querySelector('.cart-page');
    if (v.discountCode) await applyDiscountCode(v.discountCode, true);
    await addVariant(v.id, ghost, 'cart-gift');
    if (v.discountCode && !onCartPage) await applyDiscountCode(v.discountCode);
  }

  /**
   * Cumulative ladder UI — BEST PRACTICE: no block at all while nothing needs
   * doing. Gifts in the cart are their own confirmation (🎁 line, 0,00 €) and the
   * tier bar carries the progress/teaser. The block appears ONLY when the
   * customer must choose (size) or something went wrong (code refused).
   * Swapping a size = delete the gift line -> picker comes back.
   */
  function buildLadderBlock(state) {
    const G = state.cfg;
    if (state.mode !== 'ladder') return null; // 'adding' / 'removed' -> bar says it
    const pickRows = state.rows.filter((r) => r.status === 'pick');
    const refusedRows = state.rows.filter((r) => r.status === 'refused');
    if (!pickRows.length && !refusedRows.length) return null;

    const box = document.createElement('div');
    box.className = 'platinum-upsell__gift';
    const say = (cls, text, parent) => {
      const p = document.createElement('p');
      p.className = cls;
      p.textContent = text;
      (parent || box).appendChild(p);
      return p;
    };
    const sub = (s, name, x) => (s || '').replace(/\{name\}/g, name || '').replace(/\{x\}/g, x != null ? moneyFmt.format(x) : '');

    if (pickRows.length) {
      say('platinum-upsell__gift-heading', sub(G.heading || 'Izberi svoje darilo — brezplačno', pickRows[0].tier.name));
    }
    refusedRows.forEach((r) => {
      say('platinum-upsell__gift-warn', G.failText || 'Darila ni bilo mogoče pripeti.');
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'platinum-upsell__gift-remove';
      remove.textContent = G.removeText || 'Odstrani iz košarice';
      remove.addEventListener('click', () => removeGiftLines(r.ids, remove));
      box.appendChild(remove);
    });

    // pickers (one per tier that needs a choice)
    pickRows.forEach((r) => {
      const t = r.tier;
      if (t.descText) say('platinum-upsell__gift-desc', t.descText);
      const selects = [];
      for (let i = 0; i < r.remaining; i++) {
        const slot = document.createElement('div');
        slot.className = 'platinum-upsell__gift-slot';
        const thumbLink = document.createElement('a');
        thumbLink.className = 'platinum-upsell__gift-thumb';
        thumbLink.target = '_blank';
        thumbLink.rel = 'noopener';
        const thumbImg = document.createElement('img');
        thumbImg.loading = 'lazy';
        thumbImg.alt = '';
        thumbLink.appendChild(thumbImg);
        slot.appendChild(thumbLink);
        const nameLink = document.createElement('a');
        nameLink.className = 'platinum-upsell__gift-name';
        nameLink.target = '_blank';
        nameLink.rel = 'noopener';
        slot.appendChild(nameLink);
        const line = document.createElement('p');
        line.className = 'platinum-upsell__gift-line';
        slot.appendChild(line);
        const select = document.createElement('select');
        select.className = 'platinum-upsell__select';
        select.setAttribute('aria-label', 'Izberi');
        for (const v of r.variants) {
          const opt = document.createElement('option');
          opt.value = String(v.id);
          opt.textContent = v.title;
          select.appendChild(opt);
        }
        select.value = String(r.variants[(i + r.inCart) % r.variants.length].id);
        slot.appendChild(select);
        selects.push(select);
        const price = document.createElement('span');
        price.className = 'platinum-upsell__gift-price';
        const was = document.createElement('s');
        price.appendChild(was);
        const now = document.createElement('strong');
        now.textContent = G.freeLabel || 'BREZPLAČNO';
        price.appendChild(now);
        slot.appendChild(price);
        const sync = () => {
          const v = r.variants.find((x) => String(x.id) === select.value) || r.variants[0];
          thumbImg.src = imageUrl(v.image, 140);
          thumbImg.alt = v.name;
          thumbLink.href = v.url;
          nameLink.href = v.url;
          nameLink.textContent = v.name;
          line.textContent = v.desc || '';
          const p = v.price != null ? Number(v.price) : 0;
          was.textContent = p ? moneyFmt.format(p) : '';
          was.style.display = p ? '' : 'none';
        };
        sync();
        select.addEventListener('change', sync);
        box.appendChild(slot);
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'platinum-upsell__gift-cta';
      btn.textContent = G.buttonText || 'DODAJ DARILO';
      btn.addEventListener('click', async () => {
        const onCartPage = !!document.querySelector('.cart-page');
        const picked = r.variants.find((x) => String(x.id) === selects[0].value) || r.variants[0];
        const code = picked.discountCode || G.discountCode;
        if (code) await applyDiscountCode(code, true);
        for (const sel of selects) await addVariant(sel.value, btn, 'cart-gift');
        if (code && !onCartPage) await applyDiscountCode(code);
      });
      box.appendChild(btn);
      if (r.inCart > 0) say('platinum-upsell__gift-note', 'Dodano ' + r.inCart + ' od ' + r.slots + '.');
    });

    return box;
  }

  /** Ladder-level texts with the current/next tier name substituted — shaped like a legacy gift cfg. */
  function ladderCfg(L, tier, variants, next) {
    const sub = (s) => (s || '')
      .replace(/\{name\}/g, tier ? tier.name || '' : '')
      .replace(/\{next\}/g, next ? next.name || '' : '');
    return Object.assign({}, L, {
      variants,
      tierName: tier ? tier.name || '' : '',
      minSubtotal: tier ? tier.threshold : 0,
      unitPrice: tier && tier.unitPrice != null ? tier.unitPrice : L.unitPrice,
      heading: sub(L.heading),
      teaserHeading: sub(L.teaserHeading),
      teaserText: sub(L.teaserText),
      doneHeading: sub(L.doneHeading),
      doneText: sub(L.doneText),
      removedText: sub(L.removedText),
      upgradeText: sub(L.upgradeText),
      descText: sub(tier && tier.descText != null ? tier.descText : L.descText),
    });
  }

  /** Legacy single-promo block (CFG.gift, e.g. MENU akcija) — rendered as its own "AKCIJA" block below the ladder. */
  async function giftState(cart, map) {
    // v2: per-species tiers (CFG.gifts.dog / CFG.gifts.cat), customer picks the
    // reward. CFG.gift stays as the legacy single-config path (MENU akcija).
    const species = classifySpecies(cart, map);
    const G = CFG.gifts ? (species && CFG.gifts[species]) : CFG.gift;
    if (!G || !G.enabled || !Array.isArray(G.variants) || !G.variants.length) return null;
    // promo over — stop promising a gift the code will refuse
    if (G.endsAt && Date.now() > Date.parse(G.endsAt)) return null;
    if (G.startsAt && Date.now() < Date.parse(G.startsAt)) return null;
    if (!(cart.items || []).length) return null;
    if (Array.isArray(G.tiers) && G.tiers.length) return tieredGiftState(cart, G, !CFG.gifts && isCatOnly(cart, map));
    if (!CFG.gifts && isCatOnly(cart, map)) return null; // legacy MENU path: dog food only

    const ids = new Set(G.variants.map((v) => Number(v.id)));
    let base = 0;
    let inCart = 0;
    const chosen = [];
    for (const item of cart.items || []) {
      if (ids.has(Number(item.variant_id))) {
        inCart += item.quantity;
        if (isGiftLine(item)) {
          const cfgV = G.variants.find((v) => Number(v.id) === Number(item.variant_id));
          for (let n = 0; n < item.quantity; n++) chosen.push(cfgV ? cfgV.title : item.variant_title);
        }
      } else {
        // What the customer actually PAYS, not the list price. Prestige's free
        // shipping bar counts the same way, so the two thresholds can never
        // disagree in the same drawer (e.g. 30 € list − Appstle bundle = 27 €).
        base += item.final_line_price;
      }
    }
    const missing = Number(G.minSubtotal || 0) - base / 100;

    // Dropped back under the threshold with our gift still in the cart: take it
    // out again. Only lines WE added are removed (property check), so a customer
    // who deliberately bought MENU tins keeps them.
    if (missing > 0.005 && chosen.length > 0) {
      if (!giftRemoving) {
        giftRemoving = true;
        giftJustRemoved = true;
        removeGiftLines(ids).finally(() => { giftRemoving = false; });
      }
      return { cfg: G, mode: 'removed' };
    }

    if (missing > 0.005) {
      giftJustRemoved = false;
      return { cfg: G, mode: 'teaser', missing: missing };
    }
    giftJustRemoved = false;

    // Are the gift lines ACTUALLY discounted? We check the line allocations, not
    // the discount's name: that works for an automatic discount and for a code
    // alike, and it answers the only question that matters — is the tin free?
    // A code can be refused (one use per customer, usage limit, expiry) and the
    // customer would otherwise silently pay for a "gift".
    const codeApplied = (cart.items || []).some(
      (i) => ids.has(Number(i.variant_id)) && (i.line_level_discount_allocations || []).length > 0
    );

    // Live stock check — a sold-out flavour must never be offered as a gift.
    // While we have the product anyway, take its image and URL: a flavour name
    // in a dropdown means nothing to someone who doesn't know the MENU line.
    const available = [];
    for (const v of G.variants) {
      const product = v.handle ? await getProduct(v.handle) : null;
      const match = product && (product.variants || []).find((pv) => Number(pv.id) === Number(v.id));
      if (product && match && !match.available) continue;
      available.push({
        id: v.id,
        handle: v.handle,
        title: v.title,
        name: v.name || (product ? product.title.split(',')[0].trim() : v.title),
        desc: v.desc || '',
        productTitle: product ? product.title.split(',')[0].trim() : v.title,
        image: (match && match.featured_image && match.featured_image.url) || (product && product.featured_image) || '',
        url: product ? product.url || '/products/' + v.handle : '/products/' + v.handle,
      });
    }
    if (!available.length) return null;

    const slots = Number(G.slots || 2);
    const remaining = Math.max(0, slots - inCart);
    let mode = 'pick';
    if (inCart > 0 && !codeApplied) mode = 'refused';
    else if (remaining === 0) mode = 'done';
    return { cfg: G, mode, variants: available, slots, inCart, codeApplied, remaining, chosen };
  }

  /**
   * TIERED promo (AKCIJA Stars, 2026-09): the threshold counts only lines of the
   * configured product types (dog food), at what the customer PAYS (after the
   * Appstle package discount). Tiers share the same gift variants; each tier has
   * its own code (STARS1/2/3 = a fixed amount on the gift products only, so the
   * package discount on the food is never touched).
   *
   * The cart holds exactly the code of the reached tier — whether the customer
   * typed one or we applied it — and none under the first threshold, so in the
   * cart a code can't work below its tier. The customer's own tins of the gift
   * products take up slots (the code covers them); only lines WE added are
   * trimmed when the cart drops a tier. Checkout itself is Shopify's: orders that
   * slip past the cart are caught by the order alert, not here.
   */
  async function tieredGiftState(cart, G, catOnly) {
    const ids = new Set(G.variants.map((v) => Number(v.id)));
    const types = (G.basisProductTypes || []).map((s) => String(s).toLowerCase());
    const tiers = G.tiers.slice().sort((a, b) => Number(a.threshold) - Number(b.threshold));
    let base = 0;
    let giftUnits = 0;
    let allUnits = 0;
    const chosen = [];
    const have = []; // every flavour already in the cart, ours or the customer's own
    for (const item of cart.items || []) {
      if (ids.has(Number(item.variant_id))) {
        allUnits += item.quantity;
        const cfgV = G.variants.find((v) => Number(v.id) === Number(item.variant_id));
        have.push(cfgV ? cfgV.title : item.variant_title);
        if (isGiftLine(item)) {
          giftUnits += item.quantity;
          for (let n = 0; n < item.quantity; n++) chosen.push(cfgV ? cfgV.title : item.variant_title);
        }
        continue; // the gift product never counts toward its own threshold
      }
      if (isGiftLine(item)) continue;
      if (!types.length || types.includes(String(item.product_type || '').toLowerCase())) base += item.final_line_price;
    }
    const baseEur = base / 100;
    let level = 0;
    tiers.forEach((t, i) => { if (baseEur + 0.005 >= Number(t.threshold)) level = i + 1; });
    const tier = level ? tiers[level - 1] : null;
    const next = tiers[level] || null;
    const slots = tier ? Number(tier.slots || level) : 0;
    const code = tier ? String(tier.discountCode || '') : '';
    const extra = {
      tiered: true, level, next, nextLevel: level + 1, slots, discountCode: code, giftUnits, chosen, have,
      missing: next ? Number(next.threshold) - baseEur : 0,
    };

    // 1) more of OUR gift tins than this tier allows -> take the surplus out
    if (giftUnits > slots) {
      if (!giftRemoving) {
        giftRemoving = true;
        trimGiftLines(ids, slots).finally(() => { giftRemoving = false; });
      }
      return Object.assign({ cfg: G, mode: 'removed' }, extra);
    }

    // 2) exactly the reached tier's code on the cart while it holds gift tins; none otherwise
    if (syncPromoCode(cart, G, level && allUnits > 0 ? code : '')) {
      return Object.assign({ cfg: G, mode: 'syncing' }, extra);
    }

    if (!level) return catOnly ? null : Object.assign({ cfg: G, mode: 'teaser' }, extra);

    // 3) live stock check — a sold-out flavour is never offered
    const available = [];
    for (const v of G.variants) {
      const product = v.handle ? await getProduct(v.handle) : null;
      const match = product && (product.variants || []).find((pv) => Number(pv.id) === Number(v.id));
      if (product && match && !match.available) continue;
      available.push({
        id: v.id,
        handle: v.handle,
        title: v.title,
        name: v.name || (product ? product.title.split(',')[0].trim() : v.title),
        desc: v.desc || '',
        short: v.short || '',
        image: (match && match.featured_image && match.featured_image.url) || (product && product.featured_image) || '',
        url: product ? product.url || '/products/' + v.handle : '/products/' + v.handle,
        price: v.price != null ? Number(v.price) : (match && match.price != null ? match.price / 100 : null),
      });
    }
    if (!available.length && !allUnits) return null;

    const filled = Math.min(allUnits, slots);
    const remaining = available.length ? Math.max(0, slots - filled) : 0;
    // the tier's code must actually sit on the gift products (Appstle's treat
    // package discount can land there too, so match the code, not "any discount")
    const codeApplied = (cart.items || []).some((i) => ids.has(Number(i.variant_id)) &&
      (i.line_level_discount_allocations || []).some((a) => a.amount > 0 && a.discount_application &&
        String(a.discount_application.title || '').toUpperCase() === code.toUpperCase()));
    let mode = 'pick';
    if (allUnits > 0 && !codeApplied) mode = 'refused';
    else if (remaining === 0) mode = 'done';
    return Object.assign({ cfg: G, mode, variants: available, inCart: filled, remaining, codeApplied }, extra);
  }

  /**
   * Gap cards for a tiered promo: dog food that counts toward the next tier. The customer's own
   * food first (another / a bigger bag), then the configured fallbacks. Per product the cheapest
   * available size whose price still covers the gap after a package discount (gapSafety, e.g. 0.7 —
   * Appstle's "Več kupiš" lowers what an extra bag adds), else the biggest size.
   */
  async function gapTargets(cart, promo) {
    const G = promo.cfg;
    const types = (G.basisProductTypes || []).map((s) => String(s).toLowerCase());
    const isFood = (t) => !types.length || types.includes(String(t || '').toLowerCase());
    const own = [];
    (cart.items || []).forEach((i) => {
      if (!isGiftLine(i) && isFood(i.product_type) && !own.includes(i.handle)) own.push(i.handle);
    });
    const handles = own.concat((G.gapFallbackHandles || []).filter((h) => !own.includes(h)));
    const need = (promo.missing * 100) / Number(G.gapSafety || 0.7); // cents, list price
    const out = [];
    for (const h of handles) {
      const product = await getProduct(h);
      if (!product || !product.available || !isFood(product.type)) continue;
      const vs = (product.variants || []).filter((v) => v.available);
      if (!vs.length) continue;
      const covering = vs.filter((v) => v.price >= need).sort((a, b) => a.price - b.price);
      const pick = covering[0] || vs.slice().sort((a, b) => b.price - a.price)[0];
      out.push({ handle: h, variantId: pick.id, pitch: (own.includes(h) ? G.gapPitchOwn : G.gapPitch) || G.gapPitch || '' });
    }
    return out;
  }

  /** Upper-cased codes of a tiered promo. */
  function promoCodes(G) {
    return new Set((G.tiers || []).map((t) => String(t.discountCode || '').toUpperCase()).filter(Boolean));
  }

  /**
   * Put exactly `want` (or none) of the promo's codes on the cart; every other
   * code stays. /cart/update.js `discount` REPLACES the whole list (same call
   * Prestige's own discount field makes), so the other applicable codes are
   * sent along.
   */
  async function setPromoCode(G, want) {
    try {
      const cart = await fetchCart();
      const mine = promoCodes(G);
      const others = (cart.discount_codes || [])
        .filter((d) => d.applicable && !mine.has(String(d.code).toUpperCase()))
        .map((d) => d.code);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: others.concat(want ? [want] : []).join(',') }),
      });
    } catch (e) {
      console.warn('[Platinum Upsell] promo code update failed:', e);
    }
  }

  /**
   * Returns true while a code change is in flight (the caller renders nothing
   * until the refreshed cart arrives). Loop guard: at most 2 tries of the same
   * change within 30 s (sessionStorage, survives the cart-page reload) — a code
   * Shopify won't keep can't loop us. The count resets as soon as a render sees
   * the cart in the wanted state, so a customer re-typing a code gets the fix
   * every time; only changes that never stick run out of tries.
   */
  let promoSyncing = false;
  function syncPromoCode(cart, G, want) {
    const mine = promoCodes(G);
    const present = (cart.discount_codes || []).map((d) => String(d.code).toUpperCase()).filter((c) => mine.has(c));
    const ok = want ? present.length === 1 && present[0] === want.toUpperCase() : !present.length;
    if (ok) {
      try { sessionStorage.removeItem('pgift-sync'); } catch (e) {}
      return false;
    }
    if (promoSyncing) return true;
    const sig = (want || '-') + ':' + present.join(',');
    const nowMs = Date.now();
    let log = {};
    try { log = JSON.parse(sessionStorage.getItem('pgift-sync') || '{}') || {}; } catch (e) {}
    const rec = log[sig] && nowMs - log[sig].at < 30000 ? log[sig] : { n: 0, at: nowMs };
    if (rec.n >= 2) return false;
    log[sig] = { n: rec.n + 1, at: rec.at };
    try { sessionStorage.setItem('pgift-sync', JSON.stringify(log)); } catch (e) {}
    promoSyncing = true;
    setPromoCode(G, want).finally(() => {
      promoSyncing = false;
      if (document.querySelector('.cart-page')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
    return true;
  }

  /** Line quantities by line key, all in ONE request (keys of the other lines can change after each single change). */
  function postCartUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
  }

  /** Cut OUR gift tins down to `keep` (newest lines first); the customer's own tins are never touched. */
  async function trimGiftLines(ids, keep) {
    try {
      const cart = await fetchCart();
      const lines = (cart.items || []).filter((i) => ids.has(Number(i.variant_id)) && isGiftLine(i));
      let over = lines.reduce((a, i) => a + i.quantity, 0) - keep;
      const updates = {};
      for (const i of lines) {
        if (over <= 0) break;
        const cut = Math.min(over, i.quantity);
        updates[i.key] = i.quantity - cut;
        over -= cut;
      }
      if (Object.keys(updates).length) await postCartUpdates(updates);
    } catch (e) {
      console.warn('[Platinum Upsell] gift trim failed:', e);
    } finally {
      if (document.querySelector('.cart-page')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  /**
   * Add several gift tins in ONE request. On the cart page Prestige has no live
   * re-render and we reload after an add — a loop of single adds would be cut
   * short by that reload.
   */
  async function addVariants(variantIds, button, source) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id: Number(id), quantity: 1, properties: { _platinum_upsell: source } })),
          sections: sectionsToBundle.join(','),
        }),
      });
      const responseJson = await response.json();
      if (!response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
      variantIds.forEach((variantId) => {
        try {
          if (typeof gtag === 'function') {
            gtag('event', 'gift_add', { event_category: 'PLATINUM Upsell', source: source, variant_id: String(variantId) });
          }
        } catch (e) {}
        try {
          if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
            Shopify.analytics.publish('platinum_upsell_add', { variantId: variantId, source: source });
          }
        } catch (e) {}
      });
      if (document.querySelector('.cart-page')) {
        window.location.reload();
        return;
      }
      const cartContent = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      cartContent['sections'] = responseJson['sections'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
      );
    } catch (e) {
      console.warn('[Platinum Upsell] gift add failed:', e);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /**
   * Apply the promo code to the cart. `?discount=` in a storefront URL is NOT
   * auto-applied — only /discount/<CODE> sets it on the cart session (same
   * finding as the Vzorci silent-add work). Then ask the theme to re-read the
   * cart so the discount line and totals appear in the drawer.
   */
  /**
   * Take the gift lines back out. Used when the code was refused (one use per
   * customer) — the customer should not have to hunt for the lines they were
   * told would be free.
   */
  /** A tin WE put in the cart as a gift — never touch tins the customer chose. */
  function isGiftLine(item) {
    return !!(item.properties && item.properties._platinum_upsell === 'cart-gift');
  }

  async function removeGiftLines(ids, button) {
    if (button) button.disabled = true;
    try {
      const cart = await fetchCart();
      const updates = {};
      (cart.items || []).filter((i) => ids.has(Number(i.variant_id)) && isGiftLine(i)).forEach((i) => { updates[i.key] = 0; });
      // ONE request: after a single change Shopify can re-key the other lines
      // (discounts re-spread), so a loop of /cart/change.js with the old keys
      // failed with 400 and left gifts behind (QA 2026-09-21)
      if (Object.keys(updates).length) await postCartUpdates(updates);
    } catch (e) {
      console.warn('[Platinum Upsell] gift removal failed:', e);
    } finally {
      if (button) button.disabled = false;
      if (document.querySelector('.cart-page')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  async function applyDiscountCode(code, silent) {
    try {
      await fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
    } catch (e) {
      console.warn('[Platinum Upsell] discount apply failed:', e);
    }
    if (!silent) document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function buildGiftBlock(state) {
    const G = state.cfg;
    if (state.mode === 'syncing') return null; // tiered: code change in flight, refreshed cart re-renders
    // tiered = like the kapa/majica ladder: the tier bar carries teaser, progress and "done";
    // a block only when the customer must act (pick a flavour) or something went wrong
    if (state.tiered && state.mode !== 'pick' && state.mode !== 'refused') return null;
    if (state.tiered && state.mode === 'pick') return buildTieredPick(state);
    const box = document.createElement('div');
    box.className = 'platinum-upsell__gift';
    const ids = new Set(G.variants.map((v) => Number(v.id)));

    const heading = document.createElement('p');
    heading.className = 'platinum-upsell__gift-heading';
    heading.textContent = (
      (state.mode === 'teaser' && G.teaserHeading) ||
      (state.mode === 'done' && G.doneHeading) ||
      (state.tiered && (state.mode === 'removed' || state.mode === 'refused') && G.noticeHeading) ||
      G.heading ||
      'Darilo'
    ).replace('{x}', state.missing ? moneyFmt.format(state.missing) : '');
    box.appendChild(heading);

    const say = (cls, text) => {
      const p = document.createElement('p');
      p.className = cls;
      p.textContent = text;
      box.appendChild(p);
      return p;
    };
    // tiered: only lines WE added can be removed/swapped — the customer's own tins stay
    const hasOurLines = !state.tiered || state.giftUnits > 0;

    // 1 — under the threshold
    if (state.mode === 'teaser') {
      say(
        'platinum-upsell__gift-teaser',
        (G.teaserText || 'Do darila ti manjka še {x}.').replace('{x}', moneyFmt.format(state.missing))
      );
      return box;
    }

    // 4 — cart fell back under the threshold, gift taken out again
    if (state.mode === 'removed') {
      say('platinum-upsell__gift-warn', G.removedText || 'Darilo smo odstranili — košarica je spet pod pragom.');
      return box;
    }

    // code refused (already used — one per customer)
    if (state.mode === 'refused') {
      say('platinum-upsell__gift-warn', G.failText || 'Darila ni bilo mogoče pripeti.');
      if (hasOurLines) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'platinum-upsell__gift-remove';
        remove.textContent = G.removeText || 'Odstrani iz košarice';
        remove.addEventListener('click', () => removeGiftLines(ids, remove));
        box.appendChild(remove);
      }
      return box;
    }

    // 3 — gift is in the cart
    if (state.mode === 'done') {
      const line = say('platinum-upsell__gift-done', G.doneText || '');
      if (state.chosen.length) {
        const what = document.createElement('span');
        what.className = 'platinum-upsell__gift-chosen';
        what.textContent = state.chosen.join(' + ');
        line.appendChild(document.createTextNode(' '));
        line.appendChild(what);
      }
      // ladder upgrade: a better tier got unlocked after the pick — say so, the swap button does the rest
      if (state.upgradeAvailable && G.upgradeText) {
        say('platinum-upsell__gift-teaser', G.upgradeText.replace(/\{name\}/g, state.upgradeAvailable));
      }
      if (hasOurLines) {
        const swap = document.createElement('button');
        swap.type = 'button';
        swap.className = 'platinum-upsell__gift-remove';
        swap.textContent = G.swapText || 'zamenjaj';
        swap.addEventListener('click', () => removeGiftLines(ids, swap));
        box.appendChild(swap);
      }
      return box;
    }

    // 2 — threshold reached, pick the flavours. One button adds them all: the
    // gift is a single decision, not two.
    if (G.descText) say('platinum-upsell__gift-desc', G.descText);

    const selects = [];
    for (let i = 0; i < state.remaining; i++) {
      const slot = document.createElement('div');
      slot.className = 'platinum-upsell__gift-slot';

      // The gift must read as a product, not as a coupon: picture, full name,
      // one line of benefit, price — same anatomy as the upsell cards.
      const thumbLink = document.createElement('a');
      thumbLink.className = 'platinum-upsell__gift-thumb';
      thumbLink.target = '_blank';
      thumbLink.rel = 'noopener';
      const thumbImg = document.createElement('img');
      thumbImg.loading = 'lazy';
      thumbImg.alt = '';
      thumbLink.appendChild(thumbImg);
      slot.appendChild(thumbLink);

      const nameLink = document.createElement('a');
      nameLink.className = 'platinum-upsell__gift-name';
      nameLink.target = '_blank';
      nameLink.rel = 'noopener';
      slot.appendChild(nameLink);

      const line = document.createElement('p');
      line.className = 'platinum-upsell__gift-line';
      slot.appendChild(line);

      const select = document.createElement('select');
      select.className = 'platinum-upsell__select';
      select.setAttribute('aria-label', 'Izberi okus');
      for (const v of state.variants) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      }
      // a different flavour per slot, so nobody has to touch the dropdowns;
      // tiered: start on flavours not yet in the cart (the promo is about trying them)
      const fresh = state.tiered ? state.variants.filter((v) => !(state.have || []).includes(v.title)) : [];
      select.value = String((fresh.length ? fresh[i % fresh.length] : state.variants[(i + state.inCart) % state.variants.length]).id);
      slot.appendChild(select);
      selects.push(select);

      const price = document.createElement('span');
      price.className = 'platinum-upsell__gift-price';
      // struck-through value: per-variant price (ladder: kapa 6,50 vs majica 7,20) or the cfg unit price
      const was = document.createElement('s');
      price.appendChild(was);
      const now = document.createElement('strong');
      now.textContent = G.freeLabel || 'BREZPLAČNO';
      price.appendChild(now);
      slot.appendChild(price);

      const sync = () => {
        const v = state.variants.find((x) => String(x.id) === select.value) || state.variants[0];
        thumbImg.src = imageUrl(v.image, 140);
        thumbImg.alt = v.name;
        thumbLink.href = v.url;
        nameLink.href = v.url;
        nameLink.textContent = v.name;
        line.textContent = v.desc || '';
        const p = v.price != null ? Number(v.price) : (G.unitPrice ? Number(G.unitPrice) : 0);
        was.textContent = p ? moneyFmt.format(p) : '';
        was.style.display = p ? '' : 'none';
      };
      sync();
      select.addEventListener('change', sync);

      box.appendChild(slot);
    }

    // Give the gift a number — 'free' without a value still reads as a coupon.
    if (G.unitPrice && G.valueText) {
      say(
        'platinum-upsell__gift-value',
        G.valueText
          .replace('{n}', String(state.remaining))
          .replace('{unit}', moneyFmt.format(Number(G.unitPrice)))
          .replace('{total}', moneyFmt.format(Number(G.unitPrice) * state.remaining))
      );
    }


    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'platinum-upsell__gift-cta';
    btn.textContent = G.buttonText || 'DODAJ DARILO';
    btn.addEventListener('click', async () => {
      // tiered: the reached tier's code (replacing any other tier's), then all tins in one add
      if (state.tiered) {
        btn.disabled = true;
        await setPromoCode(G, state.discountCode);
        await addVariants(selects.map((sel) => sel.value), btn, 'cart-gift');
        return;
      }
      // Apply the code BEFORE adding: on the cart page addVariant() reloads the
      // page (Prestige has no live re-render there), so anything after it never runs.
      const onCartPage = !!document.querySelector('.cart-page');
      // ladder: a tier may carry its own code (e.g. MENU test tier) — take the
      // code of the variant actually picked; legacy/default = cfg-level code
      const picked = selects.length ? (state.variants || []).find((x) => String(x.id) === selects[0].value) : null;
      const code = (picked && picked.discountCode) || G.discountCode;
      if (code) await applyDiscountCode(code, true);
      for (const sel of selects) await addVariant(sel.value, btn, 'cart-gift');
      if (code && !onCartPage) await applyDiscountCode(code);
    });
    box.appendChild(btn);

    if (state.inCart > 0) {
      say('platinum-upsell__gift-note', 'Dodano ' + state.inCart + ' od ' + state.slots + '.');
    }

    return box;
  }

  /**
   * Tiered pick (Stars, 2026-09-21, Igor: "gumb ne sme biti tako debel") = flavour tiles:
   * picture, name, what it is, struck value, and a small outlined button — one tap adds
   * that flavour. No dropdown, and no second big orange button next to the checkout.
   */
  function buildTieredPick(state) {
    const G = state.cfg;
    const box = document.createElement('div');
    box.className = 'platinum-upsell__gift platinum-upsell__gift--tiles';
    const head = document.createElement('p');
    head.className = 'platinum-upsell__tiles-heading';
    const headings = G.pickHeadings || [];
    head.textContent = headings[Math.min(state.remaining, headings.length) - 1] || G.heading || '';
    box.appendChild(head);
    if (G.tilesSubText) {
      const sub = document.createElement('p');
      sub.className = 'platinum-upsell__tiles-sub';
      sub.textContent = G.tilesSubText;
      box.appendChild(sub);
    }
    const grid = document.createElement('div');
    grid.className = 'platinum-upsell__tiles';
    for (const v of state.variants) {
      const tile = document.createElement('div');
      tile.className = 'platinum-upsell__tile';
      const media = document.createElement('a');
      media.className = 'platinum-upsell__tile-img';
      media.href = v.url;
      media.target = '_blank';
      media.rel = 'noopener';
      const img = document.createElement('img');
      img.src = imageUrl(v.image, 160);
      img.alt = v.name;
      img.loading = 'lazy';
      media.appendChild(img);
      tile.appendChild(media);
      const name = document.createElement('span');
      name.className = 'platinum-upsell__tile-name';
      name.textContent = v.title;
      tile.appendChild(name);
      if (v.short) {
        const what = document.createElement('span');
        what.className = 'platinum-upsell__tile-desc';
        what.textContent = v.short;
        tile.appendChild(what);
      }
      const price = document.createElement('span');
      price.className = 'platinum-upsell__tile-price';
      if (v.price) {
        const was = document.createElement('s');
        was.textContent = moneyFmt.format(Number(v.price));
        price.appendChild(was);
        price.appendChild(document.createTextNode(' '));
      }
      const now = document.createElement('strong');
      now.textContent = G.tileFreeText || moneyFmt.format(0);
      price.appendChild(now);
      tile.appendChild(price);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'platinum-upsell__tile-btn';
      btn.textContent = G.tileButtonText || 'Izberi';
      btn.addEventListener('click', async () => {
        grid.querySelectorAll('.platinum-upsell__tile-btn').forEach((b) => { b.disabled = true; });
        await setPromoCode(G, state.discountCode);
        await addVariants([v.id], btn, 'cart-gift');
      });
      tile.appendChild(btn);
      grid.appendChild(tile);
    }
    box.appendChild(grid);
    if (state.inCart > 0) {
      const note = document.createElement('p');
      note.className = 'platinum-upsell__gift-note';
      note.textContent = 'Izbrano ' + state.inCart + ' od ' + state.slots + '.';
      box.appendChild(note);
    }
    return box;
  }

  /**
   * Safety net right above the checkout button: the threshold is reached but the
   * gift is still unclaimed. The customer was promised it in an email — do not
   * let them check out past it without noticing.
   */
  function renderCheckoutGuard(text) {
    const buttons = document.querySelectorAll(
      '#cart-drawer [name="checkout"], .cart-drawer [name="checkout"], .cart-page [name="checkout"]'
    );
    buttons.forEach((btn) => {
      const host = btn.parentElement;
      if (!host) return;
      let el = host.querySelector(':scope > .platinum-upsell__guard');
      if (!text) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement('p');
        el.className = 'platinum-upsell__guard';
        host.insertBefore(el, btn);
      }
      el.textContent = text;
    });
  }

  /**
   * Injection points: cart drawer (append after line items) and cart page
   * (insert above .cart-footer). Both can exist on /cart — each gets its own
   * container instance.
   */
  function getPlacements() {
    const out = [];
    const drawerItems = document.querySelector('#cart-drawer .cart-drawer__items');
    if (drawerItems) out.push({ host: drawerItems, ref: null, modifier: '' });
    const cartFooter = document.querySelector('.cart-page .cart-footer');
    if (cartFooter) out.push({ host: cartFooter.parentElement, ref: cartFooter, modifier: 'platinum-upsell--page' });
    return out;
  }

  /**
   * Render (or remove) the upsell block for the given cart.
   * Re-entrant calls are QUEUED, not dropped — the drawer wipes its content
   * (incl. our container) asynchronously after cart:change, so a re-render
   * request can arrive while one is in flight; dropping it would leave the
   * drawer without the upsell block (bug found in QA 2026-06-12).
   */
  async function render(cart) {
    if (rendering) {
      renderQueued = true;
      return;
    }
    rendering = true;
    try {
      if (!cart) cart = await fetchCart();
      // tiered promo: an empty cart must not keep a promo code (a gift tin added next would ride on it)
      const P = CFG.gift;
      if (!(cart.items || []).length && P && P.enabled && Array.isArray(P.tiers) && P.tiers.length) syncPromoCode(cart, P, '');

      const placements = getPlacements();
      if (!placements.length) return;

      let pairs = [];
      let headingText = HEADING;
      let gift = null;   // gift ladder (permanent)
      let promo = null;  // legacy single promo (MENU akcija) — separate block
      if (cart.items && cart.items.length > 0) {
        const map = await getMap();
        if (CFG.giftLadder && CFG.giftLadder.enabled) gift = await ladderState(cart, map, CFG.giftLadder);
        if (CFG.gift || CFG.gifts) promo = await giftState(cart, map);
        // while the promo runs the gift flavours must not also show up as paid
        // cards — "GRATIS" and "2,50 €" for the same tin, one under the other
        const giftHandles = new Set([
          ...(gift ? (gift.cfg.variants || []).map((v) => v.handle) : []),
          ...(promo ? (promo.cfg.variants || []).map((v) => v.handle) : []),
        ]);
        // tiered promo below its last tier (Stars, Igor 21. 9.): the cards help close the gap with
        // DOG FOOD (treats don't count) — max 2; back to the normal cards when the promo is over
        const gap = promo && promo.tiered && promo.next && promo.missing > 0 ? await gapTargets(cart, promo) : null;
        const picked = gap && gap.length ? { targets: gap, petTypes: new Set(['dog']) } : pickCandidates(map, cart);
        headingText = gap && gap.length
          ? ((promo.level ? promo.cfg.gapHeading : promo.cfg.gapHeadingFirst) || promo.cfg.gapHeading || HEADING)
          : pickHeading(picked.petTypes);
        const maxCards = gap && gap.length ? Number(promo.cfg.gapMaxCards || 2) : MAX_CARDS;
        for (const t of picked.targets) {
          if (pairs.length >= maxCards) break;
          if (giftHandles.has(t.handle)) continue;
          const product = await getProduct(t.handle);
          if (!product || !product.available) continue;
          pairs.push([t, product]);
        }
      }

      // audit 2026-08-21: no warning text right above the checkout button (gifts are a bonus, not a condition)
      renderCheckoutGuard(CFG.showCheckoutGuard ? ((gift && gift.hasPick) ? (gift.cfg.guardText || '') : (promo && promo.mode === 'pick') ? (promo.cfg.guardText || '') : '') : '');

      for (const place of placements) {
        const existing = place.host.querySelector(':scope > .' + CONTAINER_CLASS);
        const giftEl = gift ? buildLadderBlock(gift) : null;
        const promoEl = promo ? buildGiftBlock(promo) : null;
        if (!pairs.length && !giftEl && !promoEl) {
          if (existing) existing.remove();
          continue;
        }
        const container = existing || document.createElement('div');
        container.className = (CONTAINER_CLASS + ' ' + place.modifier).trim();
        container.innerHTML = '';

        // gift first — it is the stronger message, and it expires with the promo
        if (giftEl) container.appendChild(giftEl);
        if (promoEl) container.appendChild(promoEl);

        if (pairs.length) {
          const heading = document.createElement('p');
          heading.className = 'platinum-upsell__heading';
          heading.textContent = headingText;
          container.appendChild(heading);

          const grid = document.createElement('div');
          grid.className = 'platinum-upsell__grid';
          for (const [t, product] of pairs) {
            const card = buildCard(t, product);
            if (card) grid.appendChild(card);
          }
          container.appendChild(grid);
        }

        if (!existing) {
          if (place.ref) place.host.insertBefore(container, place.ref);
          else place.host.appendChild(container);
        }
      }
      // "Zamenjaj okus" (tier bar) took a gift out -> bring the flavour tiles into view
      let focus = null;
      try { focus = sessionStorage.getItem('pgift-focus'); } catch (e) {}
      const tiles = focus && document.querySelector('#cart-drawer .platinum-upsell__gift--tiles, .platinum-upsell--page .platinum-upsell__gift--tiles');
      if (tiles) {
        try { sessionStorage.removeItem('pgift-focus'); } catch (e) {}
        tiles.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } catch (e) {
      console.warn('[Platinum Upsell] render failed:', e);
    } finally {
      rendering = false;
      if (renderQueued) {
        renderQueued = false;
        render(); // re-run with fresh cart — a newer state arrived mid-render
      }
    }
  }

  function init() {
    render();
    // Cart changed (native or ours): re-render with fresh cart data.
    document.addEventListener('cart:change', (event) => {
      render(event.detail && event.detail.cart);
    });
    // Drawer replaced its whole content (wipes our container) — re-inject.
    document.addEventListener('cart-drawer:refreshed', () => render());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
